import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/redux-config/hooks';
import { selectUser } from '@/redux-config/entities/user';
import { Helmet } from 'react-helmet';
import { CustomBreadCrumb } from '@/components/customBreadCrumb';
import './style.scss';
import { useTranslation } from 'react-i18next';
import { CustomInput, CustomSwitch, Loading } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentType } from '@/DTO';
import { Contents } from '@/components/contents';
import { ProductApi } from '@/api';
import {
  CategoryIdComponent,
  PricingIdComponent,
  PublisherIdComponent,
} from '@/screens';
import CKeditor from '@/components/ck-editor';

export const ProductEdit: React.FC<{}> = () => {
  const user = useAppSelector(selectUser);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const crumbs = [
    { name: t('Dashboard'), href: '/' },
    { name: t('Products'), href: '#' },
    { name: t('New'), href: '#' },
  ];

  const [title, set_title] = useState<string>('');
  const [description, set_description] = useState<string>('');
  const [language, set_language] = useState<string>('');
  const [publish_date, set_publish_date] = useState<string>('');
  const [author_name, set_author_name] = useState<string>('');
  const [paper_count, set_paper_count] = useState<number>(1);
  const [offer, set_offer] = useState<number>(0);
  const [amount, set_amount] = useState<number>(1);
  const [is_auto_price, set_is_auto_price] = useState<boolean>(true);
  const [price, set_price] = useState<number>(0);
  const [pricing_id, set_pricing_id] = useState<string>();
  const [category_id, set_category_id] = useState<string>();
  const [publisher_id, set_publisher_id] = useState<string>();
  const [content, setContent] = useState<ContentType[]>([]);

  const [pricing_text, set_pricing_text] = useState<string>('');
  const [category_text, set_category_text] = useState<string>('');
  const [publisher_text, set_publisher_text] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  useEffect(() => {
    setPageLoading(true);
    ProductApi.GetProductById(user, id + '').then((data) => {
      setPageLoading(false);
      set_title(data.data?.title ? data.data.title : '');
      set_description(data.data?.description ? data.data.description : '');
      set_language(data.data?.language ? data.data.language : '');
      set_publish_date(data.data?.publish_date ? data.data.publish_date : '');
      set_author_name(data.data?.author_name ? data.data.author_name : '');
      set_paper_count(data.data?.paper_count ? data.data.paper_count : 0);
      set_offer(data.data?.offer ? data.data.offer : 0);
      set_amount(data.data?.amount ? data.data.amount : 0);
      set_is_auto_price(
        data.data?.is_auto_price ? data.data.is_auto_price : false,
      );
      set_price(data.data?.price ? data.data.price : 0);
      setContent(data.data?.content ? data.data.content : []);

      set_pricing_id(data.data?.pricing?.id ? data.data.pricing.id : '');
      set_category_id(
        data.data?.category && data.data?.category.length >= 0
          ? data.data.category[data.data.category.length - 1].id
          : '',
      );
      set_publisher_id(data.data?.publisher?.id ? data.data.publisher.id : '');

      set_pricing_text(
        data.data?.pricing
          ? `${data.data?.pricing?.size}-${data.data?.pricing?.is_multi_color ? t('multi-color') : t('black-white')}${is_auto_price ? '-' + data.data?.pricing?.price + '-' + data.data?.pricing?.bookbinding_price : ''}`
          : '',
      );
      set_category_text(
        data.data?.category && data.data?.category.length >= 0
          ? data.data.category.map((item) => item.name) + ''
          : '',
      );
      set_publisher_text(
        data.data?.publisher?.name ? data.data.publisher.name : '',
      );
    });
  }, []);

  const handleCreate = () => {
    setLoading(true);
    const content_ids = content.map((item) => item.id);
    if (id && pricing_id && category_id && publisher_id) {
      ProductApi.EditProduct(user, id, {
        title: title,
        description: description,
        language: language,
        publish_date: publish_date,
        author_name: author_name,
        paper_count: paper_count,
        offer: offer,
        amount: amount,
        is_auto_price: is_auto_price,
        price: +price,
        pricing_id: pricing_id,
        category_id: category_id,
        publisher_id: publisher_id,
        content_id: content_ids,
      })
        .then((data) => {
          navigate(`/product/info/${id + ''}`);
        })
        .catch((err) => {});
    }
  };

  return (
    <>
      <Helmet>
        <title>MahdiBookShop | Product New</title>
        <meta name="description" content="Basic example" />
      </Helmet>
      {pageLoading ? (
        <Loading />
      ) : (
        <div className="d-flex flex-column">
          <CustomBreadCrumb breadCrumbList={crumbs} />
          <div
            className={
              'd-flex flex-row flex-wrap w-100 bg-custom-white p-3 rounded-3'
            }
          >
            <h5 className={'w-100'}>{t('BasicInfo')}</h5>
            <label className={'d-flex flex-column col-12 col-md-6 p-2'}>
              <small>{t('title')}</small>
              <CustomInput
                name={'title'}
                type={'text_number'}
                className={`custom-input ${
                  title.length == 0
                    ? 'border-reg'
                    : title.length > 2
                      ? 'border-success'
                      : 'border-error'
                }`}
                value={title}
                onChange={set_title}
              />
            </label>
            <label className={'d-flex flex-column col-12 col-md-6 p-2'}>
              <small>{t('author_name')}</small>
              <CustomInput
                name={'author_name'}
                type={'text_number'}
                className={`custom-input ${
                  author_name.length == 0
                    ? 'border-reg'
                    : author_name.length >= 2
                      ? 'border-success'
                      : 'border-error'
                }`}
                value={author_name}
                onChange={set_author_name}
              />
            </label>
            <div className={'d-flex flex-column col-12  p-2'}>
              <small>{t('description')}</small>
              <CKeditor
                onChange={set_description}
                editorLoaded={!loading}
                value={description}
              />
            </div>
            <label className={'d-flex flex-column col-12 col-md-3 p-2'}>
              <small>{t('language')}</small>
              <select
                className={'custom-input'}
                onChange={(e) => {
                  set_language(e.target.value);
                }}
              >
                <option className={'d-none'}>
                  {language === 'EN' ? t('english') : t('persian')}
                </option>
                <option value={'EN'}>{t('english')}</option>
                <option value={'FA'}>{t('persian')}</option>
              </select>
            </label>

            <label className={'d-flex flex-column col-12 col-md-3 p-2'}>
              <small>{t('offer')}</small>
              <select
                className={'custom-input'}
                onChange={(e) => {
                  if (e.target.value === '0') {
                    set_offer(0);
                  } else if (e.target.value === '1') {
                    set_offer(1);
                  } else if (e.target.value === '3') {
                    set_offer(3);
                  } else if (e.target.value === '4') {
                    set_offer(4);
                  } else if (e.target.value === '5') {
                    set_offer(5);
                  }
                }}
              >
                <option className={'d-none'}>
                  {offer === 0
                    ? t('none')
                    : offer === 1
                      ? t('proposal')
                      : offer === 3
                        ? t('bestSell')
                        : offer === 4
                          ? t('bigFirst')
                          : t('header')}
                </option>
                <option value={'0'}>{t('none')}</option>
                <option value={'1'}>{t('proposal')}</option>
                <option value={'3'}>{t('bestSell')}</option>
                <option value={'4'}>{t('bigFirst')}</option>
                <option value={'5'}>{t('header')}</option>
              </select>
            </label>
            <label className={'d-flex flex-column col-12 col-md-3 p-2'}>
              <small>{t('publish_date')}</small>
              <CustomInput
                name={'publish_date'}
                type={'number'}
                className={`custom-input ${
                  publish_date.length == 0
                    ? 'border-reg'
                    : publish_date.length >= 2
                      ? 'border-success'
                      : 'border-error'
                }`}
                value={publish_date}
                onChange={set_publish_date}
              />
            </label>
            <label className={'d-flex flex-column col-12 col-md-3 p-2'}>
              <small>{t('paper_count')}</small>
              <CustomInput
                name={'paper_count'}
                type={'number'}
                className={`custom-input ${
                  paper_count == 0
                    ? 'border-reg'
                    : paper_count >= 1
                      ? 'border-success'
                      : 'border-error'
                }`}
                value={paper_count + ''}
                onChange={set_paper_count}
              />
            </label>
            <label className={'d-flex flex-column col-12 col-md-3 p-2'}>
              <small>{t('amount')}</small>
              <CustomInput
                name={'amount'}
                type={'number'}
                className={`custom-input ${
                  amount == 0
                    ? 'border-reg'
                    : amount >= 0
                      ? 'border-success'
                      : 'border-error'
                }`}
                value={amount + ''}
                onChange={set_amount}
              />
            </label>

            <PricingIdComponent
              defaultText={pricing_text}
              is_auto_price={is_auto_price}
              pricing_id={pricing_id}
              set_pricing_id={set_pricing_id}
            />
            <CategoryIdComponent
              defaultText={category_text}
              category_id={category_id}
              set_category_id={set_category_id}
            />
            <PublisherIdComponent
              defaultText={publisher_text}
              set_publisher_id={set_publisher_id}
              publisher_id={publisher_id}
            />

            <div className={'d-flex flex-column col-12 col-md-2 p-2'}>
              <small>{t('is_auto_price')}</small>
              <div>
                <CustomSwitch
                  defaultChecked={is_auto_price}
                  onClick={() => {
                    set_is_auto_price(!is_auto_price);
                    set_price(0);
                  }}
                />
              </div>
            </div>
            {!is_auto_price && (
              <label className={'d-flex flex-column col-12 col-md-4 p-2'}>
                <small>{t('price')}</small>
                <CustomInput
                  name={'price'}
                  type={'number'}
                  className={`custom-input ${
                    price == 0
                      ? 'border-reg'
                      : price >= 1000
                        ? 'border-success'
                        : 'border-error'
                  }`}
                  value={price + ''}
                  onChange={set_price}
                  disabled={is_auto_price}
                />
              </label>
            )}

            <div className={'d-flex flex-row flex-wrap w-100'}>
              <Contents
                formPrefix={'product image'}
                setContents={(data: any) => {
                  setContent(data);
                }}
                initialContents={content}
                type={'image'}
                maxSize={4}
                disabled={false}
              />
            </div>

            <div className={'w-100'}>
              <button
                className={'btn prm-btn rounded-5 px-4 mt-3'}
                disabled={
                  !(
                    title.length > 2 &&
                    author_name.length >= 2 &&
                    description.length >= 2 &&
                    language.length >= 2 &&
                    publish_date.length >= 2 &&
                    paper_count >= 1 &&
                    amount >= 0 &&
                    offer >= 0 &&
                    pricing_id &&
                    category_id &&
                    publisher_id &&
                    content.length > 0
                  ) ||
                  loading ||
                  (!is_auto_price && price < 1000)
                }
                onClick={() => {
                  handleCreate();
                }}
              >
                {t('submit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
